import Phaser from 'phaser';

import { TEXTURE_KEYS } from '@/features/letterParty/game/rendering/assets';
import LottieCanvas from '@/features/letterParty/game/rendering/lottieCanvas';

import characterLetterNewGreen from '@/features/letterParty/assets/lottie/character-letter-new-green.json';
import characterLetterNewGrey from '@/features/letterParty/assets/lottie/character-letter-new-grey.json';
import characterLetterNewBrown from '@/features/letterParty/assets/lottie/character-letter-new-brown.json';
import polonaiseIdleRight from '@/features/letterParty/assets/lottie/polonaise-idle-right.json';
import polonaiseIdleUp from '@/features/letterParty/assets/lottie/polonaise-idle-up.json';
import polonaiseIdleDown from '@/features/letterParty/assets/lottie/polonaise-idle-down.json';
import leaderIdleUp from '@/features/letterParty/assets/lottie/leader-idle-up.json';
import leaderIdleDown from '@/features/letterParty/assets/lottie/leader-idle-down.json';
import leaderIdleRight from '@/features/letterParty/assets/lottie/leader-idle-right.json';
import hatPolonaiseDown from '@/features/letterParty/assets/lottie/hat-polonaise-down.json';
import hatPolonaiseIdleDown from '@/features/letterParty/assets/lottie/hat-polonaise-idle-down.json';
import hatPolonaiseIdleRight from '@/features/letterParty/assets/lottie/hat-polonaise-idle-right.json';
import hatPolonaiseIdleUp from '@/features/letterParty/assets/lottie/hat-polonaise-idle-up.json';
import hatPolonaiseRight from '@/features/letterParty/assets/lottie/hat-polonaise-right.json';
import hatPolonaiseUp from '@/features/letterParty/assets/lottie/hat-polonaise-up.json';
import leadDown from '@/features/letterParty/assets/lottie/lead-down.json';
import leadRight from '@/features/letterParty/assets/lottie/lead-right.json';
import leadUp from '@/features/letterParty/assets/lottie/lead-up.json';
import polonaiseDown from '@/features/letterParty/assets/lottie/polonaise-down.json';
import polonaiseRight from '@/features/letterParty/assets/lottie/polonaise-right.json';
import polonaiseUp from '@/features/letterParty/assets/lottie/polonaise-up.json';

import type { Direction, LetterQ, Position } from '@/features/letterParty/types';

const GRID_COLOR = 0xe17aff;
const GRID_BACKGROUND_COLOR = 0xd043f7;
const GRID_BACKGROUND_ALPHA = 0.8;
const GRID_LINE_WIDTH = 2;
const GRID_BORDER_RADIUS = 24;
const CHARACTER_ANIMATIONS = [characterLetterNewGreen, characterLetterNewBrown, characterLetterNewGrey];
const LETTER_CHARACTER_SCALE = 1.5;
const SNAKE_CHARACTER_SCALE = 1.71;
const LETTER_CHARACTER_OFFSET_Y = -0.18;
const SNAKE_CHARACTER_OFFSET_Y = -0.15;
const CELL_DEPTH_STEP = 100;
const LETTER_PAPER_LAYER_NAME = 'Bord';

type AnimationDirection = 'UP' | 'DOWN' | 'RIGHT';
type AddBoardObject = (object: Phaser.GameObjects.GameObject | Phaser.GameObjects.GameObject[]) => void;

interface DrawSnakeConfig {
	snake: Position[];
	currentDirection: Direction;
	hatCount: number;
	originX: number;
	originY: number;
	cellSize: number;
	isMoving: boolean;
	isCollisionPause: boolean;
}

export default class LetterPartyEntityRenderer {
	constructor(
		private readonly scene: Phaser.Scene,
		private readonly lottieRenderer: LottieCanvas,
		private readonly addBoardObject: AddBoardObject,
	) {}

	public drawGrid(
		originX: number,
		originY: number,
		gridWidth: number,
		gridHeight: number,
		cellSize: number,
		pixelRatio: number,
	) {
		const graphics = this.scene.add.graphics();
		const boardWidth = gridWidth * cellSize;
		const boardHeight = gridHeight * cellSize;
		const lineWidth = GRID_LINE_WIDTH * pixelRatio;
		const radius = Math.min(GRID_BORDER_RADIUS * pixelRatio, boardWidth / 2, boardHeight / 2);

		graphics.fillStyle(GRID_BACKGROUND_COLOR, GRID_BACKGROUND_ALPHA);
		graphics.fillRoundedRect(originX, originY, boardWidth, boardHeight, radius);
		graphics.lineStyle(lineWidth, GRID_COLOR, 1);

		for (let row = 1; row < gridHeight; row++) {
			const y = originY + row * cellSize;
			graphics.lineBetween(originX, y, originX + boardWidth, y);
		}

		for (let col = 1; col < gridWidth; col++) {
			const x = originX + col * cellSize;
			graphics.lineBetween(x, originY, x, originY + boardHeight);
		}

		const borderOffset = lineWidth / 2;
		graphics.strokeRoundedRect(
			originX + borderOffset,
			originY + borderOffset,
			boardWidth - lineWidth,
			boardHeight - lineWidth,
			radius,
		);
		graphics.setDepth(1);
		this.addBoardObject(graphics);
	}

	public drawHat(position: Position, originX: number, originY: number, cellSize: number) {
		const x = this.getCellCenterX(position, originX, cellSize);
		const y = this.getCellCenterY(position, originY, cellSize);
		const depth = this.getCellDepth(position, 30);
		const size = Math.max(20, cellSize * 1.2);
		const hat = this.scene.add.image(x, y, TEXTURE_KEYS.hat).setDisplaySize(size, size).setDepth(depth);

		this.addBoardObject(hat);
	}

	public drawConfetti(position: Position, originX: number, originY: number, cellSize: number) {
		const x = this.getCellCenterX(position, originX, cellSize);
		const y = this.getCellCenterY(position, originY, cellSize);
		const depth = this.getCellDepth(position, 30);
		const size = Math.max(20, cellSize * 0.9);
		const confetti = this.scene.add.image(x, y, TEXTURE_KEYS.confetti).setDisplaySize(size, size).setDepth(depth);

		this.addBoardObject(confetti);
	}

	public drawLetterCharacter(letterQ: LetterQ, originX: number, originY: number, cellSize: number) {
		const x = this.getCellCenterX(letterQ.position, originX, cellSize);
		const y = this.getCellCenterY(letterQ.position, originY, cellSize);
		const bodySize = Math.max(20, cellSize * LETTER_CHARACTER_SCALE);
		const animationData = CHARACTER_ANIMATIONS[Math.abs(letterQ.colorSeed) % CHARACTER_ANIMATIONS.length];
		const depth = this.getCellDepth(letterQ.position, 40);

		this.lottieRenderer.add({
			key: `letter-${letterQ.position.x}-${letterQ.position.y}-${letterQ.letter}-${letterQ.colorSeed}`,
			signature: `letter-${letterQ.letter}-${letterQ.colorSeed}`,
			x,
			y,
			width: bodySize,
			height: bodySize,
			animationData,
			depth,
			offsetY: bodySize * LETTER_CHARACTER_OFFSET_Y,
			isPlaying: true,
			label: {
				text: letterQ.letter.toLowerCase(),
				offsetY: 0,
				size: bodySize,
				depth: depth + 1,
				followLayer: {
					animationData,
					layerName: LETTER_PAPER_LAYER_NAME,
				},
			},
		});
	}

	public drawSnake({
		snake,
		currentDirection,
		hatCount,
		originX,
		originY,
		cellSize,
		isMoving,
		isCollisionPause,
	}: DrawSnakeConfig) {
		snake.forEach((segment, index) => {
			const segmentAhead = index > 0 ? snake[index - 1] : undefined;
			const movingDirection =
				index === 0
					? currentDirection
					: segmentAhead
					? this.getDirectionBetween(segment, segmentAhead)
					: currentDirection;

			this.drawSnakeCharacter({
				position: segment,
				direction: movingDirection,
				originX,
				originY,
				cellSize,
				isLeader: index === 0,
				showHat: index > 0 && index <= hatCount,
				segmentIndex: index,
				isMoving,
				isCollisionPause,
			});
		});
	}

	private drawSnakeCharacter({
		position,
		direction,
		originX,
		originY,
		cellSize,
		isLeader,
		showHat,
		segmentIndex,
		isMoving,
		isCollisionPause,
	}: {
		position: Position;
		direction: Direction;
		originX: number;
		originY: number;
		cellSize: number;
		isLeader: boolean;
		showHat: boolean;
		segmentIndex: number;
		isMoving: boolean;
		isCollisionPause: boolean;
	}) {
		const x = this.getCellCenterX(position, originX, cellSize);
		const y = this.getCellCenterY(position, originY, cellSize);
		const directionOffset = this.getSegmentOffset(direction, cellSize, segmentIndex, isLeader);
		const baseX = x + directionOffset.x;
		const baseY = y + directionOffset.y;
		const bodySize = Math.max(20, cellSize * SNAKE_CHARACTER_SCALE);
		const normalizedDirection: AnimationDirection = direction === 'LEFT' ? 'RIGHT' : direction;
		const isIdleCollisionSegment = isCollisionPause;
		const animationData = this.getSnakeAnimation(normalizedDirection, isLeader, showHat, isCollisionPause);
		const depth = this.getCellDepth(position, 80);

		this.lottieRenderer.add({
			key: `snake-${segmentIndex}`,
			signature: `snake-${normalizedDirection}-${isLeader ? 'leader' : 'body'}-${showHat ? 'hat' : 'plain'}-${
				isIdleCollisionSegment ? 'collision-idle' : 'moving'
			}`,
			x: baseX,
			y: baseY,
			width: bodySize,
			height: bodySize,
			animationData,
			depth,
			offsetY: bodySize * SNAKE_CHARACTER_OFFSET_Y,
			isFlipped: direction === 'LEFT',
			isPlaying: isMoving || isIdleCollisionSegment,
		});
	}

	private getSnakeAnimation(
		direction: AnimationDirection,
		isLeader: boolean,
		showHat: boolean,
		isCollisionPause: boolean,
	) {
		if (isCollisionPause) {
			const idleAnimationMap: Record<AnimationDirection, unknown> = {
				UP: isLeader ? leaderIdleUp : showHat ? hatPolonaiseIdleUp : polonaiseIdleUp,
				DOWN: isLeader ? leaderIdleDown : showHat ? hatPolonaiseIdleDown : polonaiseIdleDown,
				RIGHT: isLeader ? leaderIdleRight : showHat ? hatPolonaiseIdleRight : polonaiseIdleRight,
			};

			return idleAnimationMap[direction];
		}
		const animationMap: Record<AnimationDirection, unknown> = {
			UP: isLeader ? leadUp : showHat ? hatPolonaiseUp : polonaiseUp,
			DOWN: isLeader ? leadDown : showHat ? hatPolonaiseDown : polonaiseDown,
			RIGHT: isLeader ? leadRight : showHat ? hatPolonaiseRight : polonaiseRight,
		};

		return animationMap[direction];
	}

	private getDirectionBetween(from: Position, to: Position): Direction {
		if (to.x > from.x) return 'RIGHT';
		if (to.x < from.x) return 'LEFT';
		if (to.y > from.y) return 'DOWN';
		return 'UP';
	}

	private getSegmentOffset(
		direction: Direction,
		cellSize: number,
		segmentIndex: number,
		isLeader: boolean,
	): Position {
		if (isLeader) {
			return { x: 0, y: 0 };
		}

		const totalOffset = segmentIndex * cellSize * 0.03;
		const offsets: Record<Direction, Position> = {
			UP: { x: 0, y: -totalOffset },
			DOWN: { x: 0, y: totalOffset },
			LEFT: { x: -totalOffset, y: 0 },
			RIGHT: { x: totalOffset, y: 0 },
		};

		return offsets[direction];
	}

	private getCellCenterX(position: Position, originX: number, cellSize: number) {
		return originX + (position.x + 0.5) * cellSize;
	}

	private getCellCenterY(position: Position, originY: number, cellSize: number) {
		return originY + (position.y + 0.5) * cellSize;
	}

	private getCellDepth(position: Position, baseDepth: number) {
		return baseDepth + position.y * CELL_DEPTH_STEP;
	}
}
